/**
 * Horário de atendimento por dia da semana — puro, testável sem Firebase.
 *
 * O formato gravado é o que `functions/messaging/expediente.js` lê para decidir
 * entre a saudação e a mensagem de ausência. Um dia gravado torto aqui vira
 * silêncio ou "estamos fechados" numa terça de manhã — e ninguém percebe uma
 * mensagem que NÃO foi enviada.
 */

/** Chave = `getDay()`: 0 é domingo. A ordem é a da semana como se lê na tela. */
export const DIAS = [
  { chave: '0', curto: 'Dom', nome: 'Domingo' },
  { chave: '1', curto: 'Seg', nome: 'Segunda-feira' },
  { chave: '2', curto: 'Ter', nome: 'Terça-feira' },
  { chave: '3', curto: 'Qua', nome: 'Quarta-feira' },
  { chave: '4', curto: 'Qui', nome: 'Quinta-feira' },
  { chave: '5', curto: 'Sex', nome: 'Sexta-feira' },
  { chave: '6', curto: 'Sáb', nome: 'Sábado' },
];

/** Sugestão de partida: Seg–Sex 08–19, Sáb 08–14, domingo fechado. */
export const PADRAO_CLINICA = {
  0: { atende: false, inicio: '08:00', fim: '19:00' },
  1: { atende: true, inicio: '08:00', fim: '19:00' },
  2: { atende: true, inicio: '08:00', fim: '19:00' },
  3: { atende: true, inicio: '08:00', fim: '19:00' },
  4: { atende: true, inicio: '08:00', fim: '19:00' },
  5: { atende: true, inicio: '08:00', fim: '19:00' },
  6: { atende: true, inicio: '08:00', fim: '14:00' },
};

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const emMinutos = (hhmm) => {
  const m = HHMM.exec(String(hhmm ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Estado do formulário a partir do que está gravado.
 *
 * Entende os DOIS modelos, como o backend: o novo (`dias`) e o antigo
 * (`diasUteis` + faixa única). Sem isso, abrir a tela numa clínica que ainda
 * está no formato antigo mostraria tudo fechado — e salvar por cima apagaria o
 * horário que estava valendo.
 */
export function paraFormulario(horario) {
  if (horario?.dias && typeof horario.dias === 'object') {
    return Object.fromEntries(
      DIAS.map(({ chave }) => {
        const janela = horario.dias[chave];
        return [
          chave,
          janela
            ? { atende: true, inicio: janela.inicio ?? '08:00', fim: janela.fim ?? '18:00' }
            : { atende: false, inicio: '08:00', fim: '18:00' },
        ];
      }),
    );
  }

  if (horario?.diasUteis || horario?.inicio) {
    const uteis = new Set((horario.diasUteis ?? [1, 2, 3, 4, 5]).map(String));
    return Object.fromEntries(
      DIAS.map(({ chave }) => [
        chave,
        {
          atende: uteis.has(chave),
          inicio: horario.inicio ?? '08:00',
          fim: horario.fim ?? '18:00',
        },
      ]),
    );
  }

  return { ...PADRAO_CLINICA };
}

/**
 * O objeto que vai para `config.horarioAtendimento`.
 *
 * Só os dias marcados entram no mapa — dia ausente é dia fechado, que é como o
 * `dentroDoExpediente` interpreta. Grava no modelo NOVO sempre: salvar a tela
 * migra a clínica do formato antigo, o que é o desejado.
 */
export function paraConfig(formulario) {
  const dias = {};
  for (const { chave } of DIAS) {
    const d = formulario?.[chave];
    if (d?.atende) dias[chave] = { inicio: d.inicio, fim: d.fim };
  }
  return { dias };
}

/** `null` quando o formulário serve; a mensagem de recusa quando não. */
export function recusarHorario(formulario) {
  for (const { chave, nome } of DIAS) {
    const d = formulario?.[chave];
    if (!d?.atende) continue;

    const inicio = emMinutos(d.inicio);
    const fim = emMinutos(d.fim);
    if (inicio == null || fim == null) return `${nome}: informe início e fim no formato HH:MM.`;
    // Fim igual ao início seria um dia aberto por zero minuto: fechado com
    // aparência de aberto, o pior dos dois.
    if (fim <= inicio) return `${nome}: o fim precisa ser depois do início.`;
  }
  return null;
}

/** Resumo legível, para a tela confirmar o que vai valer. */
export function resumir(formulario) {
  const abertos = DIAS.filter(({ chave }) => formulario?.[chave]?.atende);
  if (abertos.length === 0) return 'Nenhum dia marcado — a ausência sai a semana inteira.';
  return abertos
    .map(({ chave, curto }) => `${curto} ${formulario[chave].inicio}–${formulario[chave].fim}`)
    .join(' · ');
}
